import { Link } from "react-router-dom";
import { Sparkles, PenLine, FileDown, Volume2, Brain, ArrowRight, Star, Heart, Lock, Check } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

const floatingKana = [
  { c: "あ", cls: "top-10 left-[6%] text-sakura", d: "0s" },
  { c: "ネ", cls: "top-40 right-[8%] text-ramune", d: ".8s" },
  { c: "花", cls: "bottom-24 left-[12%] text-matcha-dark", d: "1.4s" },
  { c: "夢", cls: "top-24 left-[48%] text-lavender", d: ".4s" },
];

function Hero({ t }) {
  return (
    <section className="relative overflow-hidden">
      {floatingKana.map((k) => (
        <span key={k.c} className={`kawaii-sparkle hidden sm:inline font-jp text-4xl sm:text-6xl opacity-40 select-none ${k.cls}`} style={{ animationDelay: k.d }}>{k.c}</span>
      ))}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-14 pb-16 sm:pt-24 sm:pb-24 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7">
          <div className="kawaii-chip mb-6 rise-in"><Star className="w-3.5 h-3.5 text-sakura-deep" strokeWidth={2.5} fill="currentColor" /> {t("heroTag")}</div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.0] tracking-tight text-ink rise-in rise-in-1">
            {t("heroTitle1")}<br />
            {t("heroTitle2")} <span className="kawaii-gradient-text">{t("heroKawaii")}</span>{t("heroTitle3") === "." ? "." : ` ${t("heroTitle3")}`}
          </h1>
          <p className="mt-6 text-base sm:text-lg text-ink-soft max-w-xl leading-relaxed rise-in rise-in-2">{t("heroDesc")}</p>
          <div className="mt-8 flex flex-wrap gap-3 rise-in rise-in-3">
            <Link to="/learn/hiragana" data-testid="hero-start-btn" className="kawaii-btn kawaii-btn-primary text-base">
              <PenLine className="w-5 h-5" strokeWidth={2.5} /> {t("startHira")} <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
            </Link>
            <Link to="/pdf" data-testid="hero-pdf-btn" className="kawaii-btn text-base">
              <FileDown className="w-5 h-5" strokeWidth={2.5} /> {t("getPdfs")}
            </Link>
          </div>
          <div className="mt-8 flex flex-wrap gap-2 rise-in rise-in-4">
            {[t("heroStat1"), t("heroStat2"), t("heroStat3")].map((s) => (
              <span key={s} className="kawaii-chip"><Check className="w-3.5 h-3.5 text-matcha-dark" strokeWidth={3} /> {s}</span>
            ))}
          </div>
        </div>
        <div className="lg:col-span-5 relative flex justify-center">
          <div className="absolute inset-0 m-auto w-72 h-72 sm:w-96 sm:h-96 rounded-full premium-gradient opacity-30 blur-3xl" />
          <img src="/brand/logo.png" alt="Nihongo Kawaii mascot" className="relative w-72 sm:w-96 float-slow drop-shadow-[0_24px_40px_rgba(255,128,171,0.35)]" />
          <div className="absolute -bottom-2 right-4 sm:right-10 kawaii-card px-4 py-2.5 flex items-center gap-2 rise-in rise-in-3">
            <Heart className="w-4 h-4 text-sakura-deep" fill="currentColor" strokeWidth={2.5} />
            <span className="text-sm font-extrabold">{t("freeLine")}</span>
          </div>
        </div>
      </div>
    </section>
  );
}

function Features({ t }) {
  const features = [
    { icon: PenLine, title: t("strokeReveal"), body: t("strokeRevealBody"), color: "bg-sakura/30", span: "sm:col-span-2 lg:col-span-2" },
    { icon: Volume2, title: t("nativeAudio"), body: t("nativeAudioBody"), color: "bg-ramune/40" },
    { icon: Brain, title: t("flashcardsFeat"), body: t("flashcardsBody"), color: "bg-matcha/40" },
    { icon: FileDown, title: t("genkoyoushiPdfs"), body: t("genkoyoushiBody"), color: "bg-yuzu/40", span: "sm:col-span-2 lg:col-span-2" },
  ];
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
      <div className="mb-10">
        <div className="eyebrow mb-3">{t("everythingYouNeed")}</div>
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-tight text-ink">{t("bentoTitle")}</h2>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {features.map((f, i) => (
          <div key={f.title} className={`kawaii-card kawaii-card-hover p-7 ${f.span || ""}`} data-testid={`feature-${i}`}>
            <div className={`inline-flex items-center justify-center w-12 h-12 rounded-2xl ${f.color} mb-5`}>
              <f.icon className="w-5 h-5 text-ink" strokeWidth={2.5} />
            </div>
            <div className="font-display text-xl mb-2 text-ink">{f.title}</div>
            <p className="text-sm text-ink-soft leading-relaxed">{f.body}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function Paths({ t }) {
  const paths = [
    { to: "/learn/hiragana", char: "あ", label: t("hiragana"), desc: t("hiraDesc"), bg: "from-sakura/50 to-peach/30", count: "104", id: "hiragana" },
    { to: "/learn/katakana", char: "カ", label: t("katakana"), desc: t("kataDesc"), bg: "from-ramune/50 to-sky/30", count: "104", id: "katakana" },
    { to: "/learn/kanji", char: "花", label: `${t("kanji")} N5–N3`, desc: t("kanjiDesc"), bg: "from-matcha/60 to-mint/30", count: "137", id: "kanji" },
  ];
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
      <div className="mb-10">
        <div className="eyebrow mb-3">{t("pickPath")}</div>
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-tight text-ink">{t("whereStart")}</h2>
      </div>
      <div className="grid md:grid-cols-3 gap-5">
        {paths.map((p) => (
          <Link key={p.to} to={p.to} data-testid={`path-${p.id}`} className="kawaii-card kawaii-card-hover p-6 flex flex-col gap-5 group">
            <div className={`w-full aspect-[4/3] rounded-3xl bg-gradient-to-br ${p.bg} flex items-center justify-center relative overflow-hidden`}>
              <span className="font-jp text-7xl sm:text-8xl text-ink group-hover:scale-110 transition-transform duration-500">{p.char}</span>
              <span className="absolute top-3 right-3 kawaii-chip">{p.count} {t("charactersCount")}</span>
            </div>
            <div>
              <div className="font-display text-2xl text-ink">{p.label}</div>
              <p className="text-sm text-ink-soft mt-1">{p.desc}</p>
            </div>
            <span className="inline-flex items-center gap-1.5 font-extrabold text-sm text-sakura-deep">{t("start")} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" strokeWidth={2.5} /></span>
          </Link>
        ))}
      </div>
    </section>
  );
}

function PremiumTeaser({ t }) {
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
      <div className="kawaii-card shimmer-border p-8 sm:p-12 grid lg:grid-cols-2 gap-8 items-center overflow-hidden relative" data-testid="home-premium-teaser">
        <div>
          <div className="kawaii-chip gold-badge mb-5"><Sparkles className="w-3.5 h-3.5" strokeWidth={2.5} /> {t("premium")}</div>
          <h2 className="font-display text-3xl sm:text-4xl leading-tight text-ink">{t("premiumTeaserTitle")}</h2>
          <p className="mt-4 text-ink-soft leading-relaxed">{t("premiumTeaserBody")}</p>
          <Link to="/pricing" data-testid="home-pricing-btn" className="kawaii-btn kawaii-btn-ink mt-7">
            {t("viewPlans")} <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
          </Link>
        </div>
        <div className="grid grid-cols-3 gap-3">
          {["日", "月", "花", "愛", "夢", "星"].map((c, i) => (
            <div key={c} className={`kawaii-card aspect-square flex items-center justify-center relative ${i % 2 ? "bg-white/50" : "bg-white/80"}`}>
              <span className="font-jp text-4xl text-ink/80">{c}</span>
              {i > 2 && <Lock className="absolute top-2 right-2 w-3.5 h-3.5 text-ink/40" strokeWidth={2.5} />}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const { t } = useLang();
  return (
    <div>
      <Hero t={t} />
      <Features t={t} />
      <Paths t={t} />
      <PremiumTeaser t={t} />
      <footer className="mt-16 mb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 flex items-center justify-between flex-wrap gap-3 border-t border-ink/5">
          <div className="flex items-center gap-2 font-display text-ink">
            <img src="/brand/logo.png" alt="Nihongo Kawaii" className="w-8 h-8 rounded-full object-cover" /> Nihongo Kawaii
          </div>
          <div className="text-sm text-ink-soft">{t("footerTag")}</div>
        </div>
      </footer>
    </div>
  );
}
